import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { createFileRoute } from "@tanstack/react-router";
import { AccountShell } from "@/components/customer/AccountShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCustomerWallet, useCustomerWalletTransactions } from "@/hooks/useCustomerWallet";
import { formatISTDateTime } from "@/lib/date/ist";
import { formatPaise } from "@/lib/format/money";
import { requestWalletTopup, verifyWalletTopup } from "@/lib/public/mp2-customer-wallet.functions";
import { loadRazorpayScript } from "@/lib/payments/razorpay-client";

export const Route = createFileRoute("/_customer/account/wallet")({
  component: WalletPage,
  head: () => ({
    meta: [
      { title: "Wallet | Poonam's Healthy Kitchen" },
      {
        name: "description",
        content: "View your wallet balance, credits, refunds and transaction history.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
});

const PAGE_SIZE = 25;

const TXN_TYPES = [
  { value: "all", label: "All types" },
  { value: "credit_refund", label: "Refund credit" },
  { value: "credit_topup", label: "Top-up credit" },
  { value: "credit_adjustment", label: "Adjustment credit" },
  { value: "credit_promo", label: "Promo credit" },
  { value: "debit_redemption", label: "Redemption debit" },
  { value: "debit_adjustment", label: "Adjustment debit" },
];

function esc(v: unknown): string {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function downloadBlob(content: BlobPart, mime: string, filename: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function printHtml(html: string): void {
  if (typeof document === "undefined") return;
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.position = "fixed";
  frame.style.right = "0";
  frame.style.bottom = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  document.body.appendChild(frame);
  const win = frame.contentWindow;
  const docu = frame.contentDocument;
  if (!win || !docu) {
    frame.remove();
    return;
  }
  docu.open();
  docu.write(html);
  docu.close();
  const run = () => {
    win.focus();
    win.print();
    window.setTimeout(() => frame.remove(), 1000);
  };
  if (docu.readyState === "complete") window.setTimeout(run, 60);
  else frame.onload = () => window.setTimeout(run, 60);
}

function WalletPage() {
  const { wallet, isLoading: walletLoading, isError: walletError } = useCustomerWallet();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [type, setType] = useState("all");

  const [topupAmount, setTopupAmount] = useState<string>("1000");
  const [topupLoading, setTopupLoading] = useState(false);
  const [isTopupOpen, setIsTopupOpen] = useState(false);
  const requestTopupFn = useServerFn(requestWalletTopup);
  const verifyTopupFn = useServerFn(verifyWalletTopup);

  const { data, isLoading, isFetching, isError } = useCustomerWalletTransactions({
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    search: search.trim() || null,
    from: from || null,
    to: to || null,
    type: type === "all" ? null : type,
  });

  const rows = data?.rows ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const tableHeaders = useMemo(
    () => [
      "Date",
      "Reference No",
      "Order No",
      "Type",
      "Description",
      "Credit",
      "Debit",
      "Balance After",
      "Status",
      "Created By",
    ],
    [],
  );

  function rowCells(t: (typeof rows)[number]): string[] {
    return [
      formatISTDateTime(t.created_at),
      t.reference_no ?? "—",
      t.order_ref ?? "—",
      t.txn_type ?? "—",
      t.description ?? "—",
      t.direction === "credit" ? formatPaise(t.amount_paise) : "",
      t.direction === "debit" ? formatPaise(t.amount_paise) : "",
      formatPaise(t.balance_after_paise),
      t.status ?? "—",
      t.created_by_label ?? "—",
    ];
  }

  function exportCsv() {
    const lines = [tableHeaders, ...rows.map(rowCells)];
    const csv = lines
      .map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\r\n");
    downloadBlob(csv, "text/csv;charset=utf-8;", "wallet-transactions.csv");
  }

  function exportExcel() {
    const html = `<html><head><meta charset="utf-8"/></head><body><table border="1">
      <thead><tr>${tableHeaders.map((h) => `<th>${esc(h)}</th>`).join("")}</tr></thead>
      <tbody>${rows
        .map((t) => `<tr>${rowCells(t).map((c) => `<td>${esc(c)}</td>`).join("")}</tr>`)
        .join("")}</tbody>
    </table></body></html>`;
    downloadBlob(html, "application/vnd.ms-excel", "wallet-transactions.xls");
  }

  function printTransactions() {
    const html = `<!doctype html><html><head><meta charset="utf-8"/>
      <title>Wallet transactions</title>
      <style>
        @page { margin: 12mm; }
        body { font-family: ui-sans-serif, system-ui, Arial, sans-serif; color: #111; font-size: 12px; }
        h1 { font-size: 16px; }
        table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        th, td { border: 1px solid #bbb; padding: 5px 6px; text-align: left; }
        th { background: #f3f3f3; }
      </style></head><body>
      <h1>Wallet transactions — Poonam's Healthy Kitchen</h1>
      <table>
        <thead><tr>${tableHeaders.map((h) => `<th>${esc(h)}</th>`).join("")}</tr></thead>
        <tbody>${rows
          .map((t) => `<tr>${rowCells(t).map((c) => `<td>${esc(c)}</td>`).join("")}</tr>`)
          .join("")}</tbody>
      </table>
      <p>Printed ${esc(new Date().toLocaleString("en-IN"))}</p>
      </body></html>`;
    printHtml(html);
  }

  const handleTopup = async () => {
    const paise = parseInt(topupAmount) * 100;
    if (isNaN(paise) || paise < 10000) {
      toast.error("Minimum top-up is ₹100");
      return;
    }

    setTopupLoading(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) throw new Error("Razorpay failed to load");

      const res = await requestTopupFn({ data: { amount_paise: paise } });
      if (!res.ok) throw new Error(res.error || "Failed to create topup request");

      const rzp = new window.Razorpay({
        key: res.key,
        amount: res.amount_paise,
        order_id: res.gateway_order_id,
        name: "Wallet Top-up",
        description: `Credit ₹${paise / 100} to your wallet`,
        prefill: res.prefill,
        handler: async (response: any) => {
          setTopupLoading(true);
          try {
            const verifyRes = await verifyTopupFn({
              data: {
                amount_paise: paise,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              }
            });

            if (!verifyRes.ok) {
              if (verifyRes.error === "DATABASE_MIGRATION_REQUIRED_BLOCKED") {
                toast.error("Wallet credit BLOCKED: Database migration required.");
              } else {
                toast.error("Payment verification failed. Please contact support.");
              }
              return;
            }

            toast.success("Payment successful! Wallet credited.");
            window.location.reload();
          } catch (vErr) {
            console.error(vErr);
            toast.error("Verification error. Please check your history in a moment.");
          } finally {
            setTopupLoading(false);
          }
        },
      });
      rzp.open();
    } catch (err: any) {
      console.error("[wallet] topup failed", err);
      toast.error(err.message || "Failed to initiate top-up");
    } finally {
      setTopupLoading(false);
    }
  };

  return (
    <AccountShell title="Wallet" description="Your wallet balance, credits, refunds and transaction history.">
      <div className="mb-6 rounded-xl border border-border/60 bg-card p-6 text-center">
        <p className="text-lg font-semibold">Wallet is currently unavailable.</p>
        <p className="mt-2 text-sm text-muted-foreground">
          New wallet transactions and top-ups are temporarily disabled.
        </p>
      </div>

      {false && walletLoading ? (
        <Skeleton className="h-40 w-full rounded-xl" />
      ) : walletError || !wallet ? (
        <p role="alert" className="rounded-lg border border-border/60 bg-card p-6 text-sm text-muted-foreground">
          Could not load your wallet. Please try again.
        </p>
      ) : (
        <>
          {wallet.is_frozen ? (
            <div
              role="alert"
              className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive"
            >
              Your wallet is frozen{wallet.frozen_reason ? `: ${wallet.frozen_reason}` : "."}
              {" "}Contact support for assistance.
            </div>
          ) : null}

          <section className="rounded-xl border border-border/60 bg-card p-5 md:p-6">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Current balance
            </p>
            <p className="mt-1 font-display text-3xl font-semibold tracking-tight md:text-4xl">
              {formatPaise(wallet.balance_paise)}
            </p>

            {false && (
              <div className="mt-4 flex flex-col sm:flex-row max-w-lg gap-4">
                {!isTopupOpen ? (
                  <Button onClick={() => setIsTopupOpen(true)}>Add Money</Button>
                ) : (
                  <>
                    <div className="flex-1 space-y-1.5">
                      <Label htmlFor="topup-amount" className="sr-only">Amount</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                        <Input
                          id="topup-amount"
                          type="number"
                          min="100"
                          step="100"
                          className="pl-7"
                          value={topupAmount}
                          onChange={(e) => setTopupAmount(e.target.value)}
                          placeholder="1000"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleTopup} disabled={topupLoading}>
                        {topupLoading ? "Processing..." : "Top-up Now"}
                      </Button>
                      <Button variant="ghost" onClick={() => setIsTopupOpen(false)} disabled={topupLoading}>
                        Cancel
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}


            <div className="mt-6 grid grid-cols-1 gap-4 border-t border-border/60 pt-5 sm:grid-cols-3">
              <div>
                <p className="text-xs text-muted-foreground">Lifetime credits</p>
                <p className="mt-1 text-lg font-semibold">{formatPaise(wallet.lifetime_credit_paise)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Lifetime used</p>
                <p className="mt-1 text-lg font-semibold">{formatPaise(wallet.lifetime_debit_paise)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Refund pending</p>
                <p className="mt-1 text-lg font-semibold">{formatPaise(wallet.pending_refund_paise)}</p>
              </div>
            </div>
          </section>

          <section className="mt-6 rounded-xl border border-border/60 bg-card p-5 md:p-6">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[180px] flex-1 space-y-1.5">
                <Label htmlFor="wallet-search">Search</Label>
                <Input
                  id="wallet-search"
                  placeholder="Reference, order no, description…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wallet-from">From</Label>
                <Input
                  id="wallet-from"
                  type="date"
                  value={from}
                  onChange={(e) => {
                    setFrom(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wallet-to">To</Label>
                <Input
                  id="wallet-to"
                  type="date"
                  value={to}
                  onChange={(e) => {
                    setTo(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div className="min-w-[180px] space-y-1.5">
                <Label htmlFor="wallet-type">Type</Label>
                <Select
                  value={type}
                  onValueChange={(v) => {
                    setType(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger id="wallet-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TXN_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
                  Export CSV
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={exportExcel} disabled={rows.length === 0}>
                  Export Excel
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={printTransactions} disabled={rows.length === 0}>
                  Print
                </Button>
              </div>
            </div>

            <div className="mt-5">
              {isLoading ? (
                <div className="space-y-2" aria-busy="true" aria-live="polite">
                  <span className="sr-only">Loading wallet transactions</span>
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : isError ? (
                <p role="alert" className="p-6 text-center text-sm text-muted-foreground">
                  Could not load your wallet transactions. Please try again.
                </p>
              ) : rows.length === 0 ? (
                <div className="p-8 text-center">
                  <h2 className="text-base font-semibold">No transactions yet</h2>
                  <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
                    Wallet credits, refunds and redemptions will appear here.
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      {tableHeaders.map((h) => (
                        <TableHead key={h}>{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell className="whitespace-nowrap">{formatISTDateTime(t.created_at)}</TableCell>
                        <TableCell className="whitespace-nowrap font-mono text-xs">
                          {t.reference_no ?? "—"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">{t.order_ref ?? "—"}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          <Badge variant="secondary">{t.txn_type ?? "—"}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[220px] truncate" title={t.description ?? undefined}>
                          {t.description ?? "—"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-emerald-600">
                          {t.direction === "credit" ? formatPaise(t.amount_paise) : ""}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-destructive">
                          {t.direction === "debit" ? formatPaise(t.amount_paise) : ""}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">{formatPaise(t.balance_after_paise)}</TableCell>
                        <TableCell className="whitespace-nowrap">{t.status ?? "—"}</TableCell>
                        <TableCell className="whitespace-nowrap">{t.created_by_label ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {rows.length > 0 ? (
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-border/60 pt-4">
                <p className="text-xs text-muted-foreground" aria-live="polite">
                  Page {page} of {totalPages} · {totalCount} {totalCount === 1 ? "transaction" : "transactions"} in
                  total
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1 || isFetching}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages || isFetching}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </section>
        </>
      )}
    </AccountShell>
  );
}
